/**
 * Agent Directory Page Content
 * All static content for the /agents directory page
 * 
 * USAGE: Import this in +Page.tsx and render content from here
 * DO NOT hardcode any text in the component - use this file
 */

export const agentDirectoryContent = {
  // ===================
  // SEO METADATA
  // ===================
  seo: {
    title: 'Find Real Estate Agents in Kenya | Afriqahome',
    description: 'Browse verified real estate agents in Kenya. Find trusted property experts in Nairobi, Mombasa, and across Kenya. Connect directly via WhatsApp.',
    canonical: 'https://www.afriqahome.com/agents',
    ogImage: 'https://www.afriqahome.com/og-image.png',
  },

  // ===================
  // HERO SECTION
  // ===================
  hero: {
    title: 'Find Your Real Estate Agent',
    subtitle: 'Connect with verified property experts across Kenya',
    trustLine: 'Every agent is reviewed by Afriqahome before joining our network',
  },

  // ===================
  // SORTING OPTIONS
  // ===================
  sortOptions: [
    { value: 'featured', label: 'Featured' },
    { value: 'name-asc', label: 'Name (A-Z)' },
    { value: 'name-desc', label: 'Name (Z-A)' },
  ],

  // ===================
  // AGENT CARD LABELS
  // ===================
  cardLabels: {
    verified: 'Verified',
    independent: 'Independent Agent',
    viewProfile: 'View Profile',
    whatsapp: 'WhatsApp',
    contact: 'Contact',
  },

  // ===================
  // EMPTY STATE (if no agents match criteria)
  // ===================
  emptyState: {
    title: "We're growing our network",
    description: "Can't find what you're looking for? Tell us your requirements and we'll connect you with the right agent.",
    ctaText: 'Get Help Finding an Agent',
    ctaWhatsApp: '+254700000000', // UPDATE: Replace with actual WhatsApp number
  },

  // ===================
  // BOTTOM CTA SECTION
  // ===================
  bottomCta: {
    title: "Can't find the right agent?",
    description: "Tell us what you need — property type, budget, location — and we'll personally connect you with an agent who matches.",
    ctaText: 'Get Matched with an Agent',
    ctaWhatsApp: '+254700000000', // UPDATE: Replace with actual WhatsApp number
  },

  // ===================
  // FAQ SECTION
  // ===================
  faq: {
    title: 'Frequently Asked Questions',
    items: [
      {
        question: 'How do I choose the right agent?',
        answer: "Look for agents who specialize in your target area and property type. Check their bio for experience and read their approach. A good fit means they understand your needs — whether you're a first-time buyer, diaspora investor, or looking for commercial property.",
      },
      {
        question: 'What does "Verified" mean?',
        answer: "Verified agents have been reviewed by Afriqahome. We confirm their identity, check their track record, and ensure they meet our professional standards. While verification doesn't guarantee outcomes, it means we've done baseline due diligence so you don't have to start from zero.",
      },
      {
        question: 'How do I contact an agent?',
        answer: "Click the WhatsApp button on any agent card to start a conversation directly. You can also view their full profile for more details before reaching out. Agents typically respond within a few hours during business hours.",
      },
      {
        question: 'Do I pay Afriqahome to use an agent?',
        answer: "No. Afriqahome is free for property seekers. You work directly with the agent you choose. Any fees or commissions are between you and your agent — we recommend discussing this upfront before starting your search.",
      },
      {
        question: 'Can I work with multiple agents?',
        answer: "Yes, you can contact multiple agents. However, for the best experience, we recommend being upfront about it. If you're seriously searching, working closely with one or two agents who understand your needs often yields better results than spreading too thin.",
      },
    ],
  },

  // ===================
  // AGENT CTA (for agents visiting the page)
  // ===================
  agentCta: {
    title: 'Are you a real estate agent?',
    description: 'Join our network and connect with property seekers across Kenya.',
    ctaText: 'List Your Services',
    ctaLink: '/auth?mode=signup&role=agent',
  },

  // ===================
  // FEATURED AGENT ORDER
  // Manual sort order for "Featured" view
  // Array of agent slugs in display order
  // ===================
  featuredOrder: [
    'james-maina',
    'njeri-kamau',
    'aisha-warsame',
    'daniel-karanja',
    'grace-wambui',
    'elizabeth-chebet',
    'hassan-omar',
    'brian-kiprop',
    'wanjiku-ndungu',
    'samuel-mutua',
    'kevin-otieno',
    'dirie-abukar-kenya-82288e73',
    'diri-nairobi-6dcc625c',
    'diri-nairobi-170b6d9b',
  ],

  // ===================
  // SCHEMA.ORG STRUCTURED DATA
  // ===================
  schema: {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Real Estate Agents in Kenya',
    description: 'Directory of verified real estate agents on Afriqahome',
    url: 'https://www.afriqahome.com/agents',
    numberOfItems: 14, // UPDATE: Keep in sync with actual count
    itemListOrder: 'https://schema.org/ItemListUnordered',
    // Individual items will be generated dynamically from agent data
  },
};

// ===================
// TYPE DEFINITIONS
// ===================
export interface Agent {
  id: string;
  full_name: string;
  slug: string;
  avatar_url: string | null;
  bio: string | null;
  phone: string | null;
  email: string;
  agency_name: string | null;
  is_independent: boolean;
  location: string | null;
  is_verified: boolean;
  tier: 'ignite' | 'elevate' | 'dominate';
  last_active_at: string | null;
  created_at: string;
  listing_count?: number;
}

export interface AgentCardProps {
  agent: Agent;
  priority?: boolean; // For image loading priority (first 6 cards)
}

// ===================
// HELPER: Generate WhatsApp URL
// ===================
export const generateWhatsAppUrl = (phone: string | null, agentName: string): string => {
  if (!phone) return '#';
  
  // Clean phone number (remove spaces, ensure + prefix)
  const cleanPhone = phone.replace(/\s/g, '').replace(/^0/, '+254');
  const message = encodeURIComponent(
    `Hi ${agentName}, I found you on Afriqahome and I'm interested in discussing my property search.`
  );
  
  return `https://wa.me/${cleanPhone.replace(/^\+/, '')}?text=${message}`;
};

// ===================
// HELPER: Truncate bio for card display
// ===================
export const truncateBio = (bio: string | null, maxLength: number = 150): string => {
  if (!bio) return '';
  if (bio.length <= maxLength) return bio;
  
  // Find last space before maxLength to avoid cutting words
  const truncated = bio.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  return truncated.substring(0, lastSpace) + '...';
};

// ===================
// HELPER: Generate initials for avatar fallback
// ===================
export const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

export default agentDirectoryContent;
